import "./styles.css";
import "./editorial.css";

import { registerRoutes } from "./routes.js";
import { masthead, banner, siteFooter } from "./ui/chrome.js";
import { h } from "./ui/dom.js";
import { notFound, start } from "./router.js";
import { initTheme, loadThemeCatalog } from "./ui/theme.js";
import { installCommandPalette } from "./ui/command-palette.lazy.js";
import { installClientErrorReporter } from "./telemetry/client-errors.js";
import { BUILD_STAMP } from "./config.js";

/*
 * Anonymous client error reporting (same-origin, no personal data).
 * Catches uncaught errors and unhandled rejections and posts to /api/client-errors.
 * No-op when VITE_CLIENT_ERRORS=0. Installed after the static imports above have
 * evaluated, so an error thrown while they load is not reported.
 */
installClientErrorReporter();

/*
 * The Chroma theme is chosen before anything renders: a stored preference,
 * else `prefers-color-scheme`. It runs here rather than in an inline <script>
 * in index.html because the site's CSP is a bare `script-src 'self'` with no
 * `unsafe-inline` — this module is the first thing the page executes, so the
 * attribute is on <html> before the first paint of any view.
 */
initTheme();

// Then the published catalog (docs/theme/theme-catalog-as-data-2026-09.md): a theme added or
// removed there reaches the pickers with no build. Failure keeps the bundled snapshot, silently.
void loadThemeCatalog();

// The ⌘K command line (ui/command-palette.ts): the theme site-wide, a page's own commands on top.
installCommandPalette();

// A slow external font stylesheet must not block module execution or the
// first board. Download it under non-screen media, then apply it once loaded.
// The listener lives here because our CSP forbids inline onload handlers.
const fonts = document.querySelector<HTMLLinkElement>('link[data-deferred-fonts]');
if (fonts) {
  const applyFonts = (): void => { fonts.media = 'all'; };
  fonts.addEventListener('load', applyFonts, { once: true });
  if (fonts.sheet) applyFonts(); // The stylesheet may have beaten this module.
}

/*
 * Build provenance, one line, at load. vite.config.ts bakes __BUILD_SHA__ into
 * the bundle (GITHUB_SHA in CI, else the checkout's HEAD), so "which commit is
 * this deployment?" — the question #2279 had to answer with a descriptor-blob
 * diff — is readable from the console of any page. The value is a string
 * literal after the define substitution: no work, nothing to await, nothing to
 * fetch.
 */
console.info(BUILD_STAMP);

registerRoutes();

notFound((_r, mount) => {
  mount.append(
    h(
      "div",
      { class: "shell" },
      masthead(),
      h(
        "section",
        { class: "section" },
        banner("There is nothing at this address."),
        h("p", {}, h("a", { href: "/", text: "Back to the tournaments" })),
      ),
      siteFooter(),
    ),
  );
});

const app = document.getElementById("app");
if (app) start(app);
